// ===== 出荷詳細 =====

import { Box, Button, Divider, Paper, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PageContext } from "../PageContext";
import Time from "../../../util/Time";
import { InfoList, InfoListColDef } from "../../../components/InfoList";
import StyledButton from "../../../components/StyledButton";
import { idForDisplay } from "../../../util/uuidTextConverter";
import { materialSearchUrl } from "../../../util/Materialperser";
import { userInfoCol } from "../../../util/userDisplay";
import { DetailGuard, useDetailFetch, useDetailRetry } from "../../../components/DetailFetch";
import { carryOutRow } from "../../../schema/console/carry_out";
import { ConstituentsById, MaterialsById, activeLots, carryOutStatusText, isLotShipped, plannedTotal } from "./helpers";
import { LotTable } from "./LotTable";

export function CarryOutDetailPage({ pageContext, materialsById, constituentsById }: { pageContext: PageContext; materialsById: MaterialsById; constituentsById: ConstituentsById }) {
  const api = pageContext.api;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [retryCount, retry] = useDetailRetry();
  const state = useDetailFetch<carryOutRow>(() => {
    if (!id) {
      return Promise.resolve(null);
    }
    return api.carry_out.getFromId({ id });
  }, [api, id, retryCount]);

  return (
    <DetailGuard state={state} notFoundLabel={`出荷 ${id}`} onRetry={retry}>
      {(co) => (
        <CarryOutDetailView co={co} navigate={navigate} materialsById={materialsById} constituentsById={constituentsById} />
      )}
    </DetailGuard>
  );
}

function CarryOutDetailView({ co, navigate, materialsById, constituentsById }: {
  co: carryOutRow; navigate: ReturnType<typeof useNavigate>; materialsById: MaterialsById; constituentsById: ConstituentsById;
}) {
  const lots = activeLots(co);
  const shippedAt = lots.length > 0 && lots.every(isLotShipped)
    ? lots.reduce((max, lot) => Math.max(max, lot.shipped_at ?? 0), 0)
    : 0;
  const allSerials = lots.flatMap((lot) => lot.Materials.map((m) => m.id));
  const totalPlanned = plannedTotal(co);
  const allAttached = totalPlanned > 0 && allSerials.length === totalPlanned;

  const infoCols: InfoListColDef[] = [
    { label: "ID", context: idForDisplay(co.id) },
    { label: "出荷状況", context: carryOutStatusText(co) },
    { label: "出荷日時", context: shippedAt > 0 ? new Time(shippedAt).Format("YYYY/MM/dd HH:mm:ss") : "-" },
    { label: "貼付済材料", context: `${allSerials.length} 件`, targetUrl: allSerials.length > 0 ? new URL(materialSearchUrl(allSerials), window.location.origin) : undefined },
    { label: "登録日", context: new Time(co.createdAt).Format("YYYY/MM/dd HH:mm:ss") },
    userInfoCol(co.User),
    { label: "備考", context: co.remarks ?? "" },
  ];

  return (
    <Stack p={3} gap={3} maxWidth={1200} mx="auto">
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h4" fontWeight="bold">出荷情報</Typography>
        <Box flex={1} />
        <StyledButton buttonStyle="primary" onClick={() => navigate("edit/")} sx={{ width: 120 }}>編集</StyledButton>
      </Stack>
      <InfoList cols={infoCols} columns={3} />
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6" fontWeight="bold">材料ロット</Typography>
          <Button variant="contained" onClick={() => navigate(`lot/new/`)}>ロット追加</Button>
        </Stack>
        <Divider sx={{ mb: 1 }} />
        <LotTable co={co} materialsById={materialsById} constituentsById={constituentsById} detailed />
      </Paper>
      {lots.length > 0 && (
        <Stack alignItems="center">
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {allAttached
              ? `✓ 配下 ${lots.length} ロット 全数貼付済 (${allSerials.length} / ${totalPlanned} 件)`
              : `配下 ${lots.length} ロット 合計 ${allSerials.length} / ${totalPlanned} 件貼付済。残りは上の表の「QR読込」ボタンから各ロット個別に読み取ってください。`}
          </Typography>
        </Stack>
      )}
    </Stack>
  );
}
